import { useImages } from "@features/images/use-images";
import { useUpgradeNeededModal } from "@features/modals/use-upgrade-needed-modal";
import { useUser } from "@features/user/use-user";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { Button } from "@radix-ui/themes";
import { Check } from "lucide-react";
import posthog from "posthog-js";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  PinterestIcon,
  PinterestShareButton,
  TwitterShareButton,
  VKIcon,
  VKShareButton,
  XIcon,
} from "react-share";
import { ResponsiveModal } from "./responsive-modal";

let lastMove = 0;
window.addEventListener("mousedown", () => {
  lastMove = Date.now();
});

export const UpgradeNeededModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, claimFreeCredits } = useUser();
  const plan = user.data?.plan || "free";
  const { isOpen, minimumPlan, closeModal, minimumCredits } =
    useUpgradeNeededModal();
  const {
    images: { data: images },
  } = useImages("favorite:1");

  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  // Check if user has already shared
  const hasShared = useMemo(() => {
    return user.data?.free_credits_actions?.includes("share") || false;
  }, [user.data?.free_credits_actions]);

  const handleShare = () => {
    if (!hasShared) {
      setTimeout(() => {
        // Start checking if user comes back to the window
        const checkInterval = setInterval(() => {
          if (lastMove && Date.now() - lastMove < 2000) {
            clearInterval(checkInterval);
            // User is back, wait 3 seconds then claim credits
            setTimeout(async () => {
              posthog.capture("claim_free_credits", {});
              const result = await claimFreeCredits("share");
              if (result) {
                console.log("Credits claimed:", result.credits);
              }
            }, 1000);
          }
        }, 500); // Check every 500ms
      }, 3000);
    }
  };

  const handleUpgrade = () => {
    closeModal();
    navigate("/billing");
  };

  useEffect(() => {
    if (isOpen)
      posthog.capture("click_upgrade_needed", { type: "upgrade_modal" });
  }, [isOpen]);

  // Get available images (limit to first 10)
  const availableImages = useMemo(() => {
    return (
      images?.filter((img) => img.status === "completed").slice(0, 10) || []
    );
  }, [images]);

  // Get random share message
  const getShareMessage = (hasImage: boolean) => {
    const messages = hasImage
      ? [
          t("upgrade_modal.share_messages.with_image_1"),
          t("upgrade_modal.share_messages.with_image_2"),
        ]
      : [
          t("upgrade_modal.share_messages.without_image_1"),
          t("upgrade_modal.share_messages.without_image_2"),
        ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const shareUrl = "https://photographe.ai";
  const selectedImage = availableImages.find(
    (img) => img.id === selectedImageId
  );
  const shareMessage = getShareMessage(!!selectedImage);
  // If an image is selected, share the image URL directly, otherwise share the main site
  const urlToShare = selectedImage?.url || shareUrl;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={() => closeModal()}
      title={t("upgrade_modal.title")}
      height="auto"
      maxHeight="80vh"
      className="!max-w-4xl"
      contentClassName="!p-0"
    >
      <div className="grid md:grid-cols-2 gap-0 md:m-0 -m-4">
        {/* Left Column - Free Option */}
        <div className="md:border-r border-slate-200 dark:border-slate-700 flex flex-col md:block hidden">
          <div className="flex-1 p-8 space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">
                {t("upgrade_modal.free_section.title")}
              </h3>
            </div>
            <div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                {t("upgrade_modal.free_section.description")
                  .split("<bold>")
                  .map((part, i) =>
                    i === 0 ? (
                      part
                    ) : (
                      <>
                        <span className="font-bold text-slate-900 dark:text-white">
                          {part.split("</bold>")[0]}
                        </span>
                        {part.split("</bold>")[1]}
                      </>
                    )
                  )}
              </p>

              {availableImages.length > 0 && (
                <div className="mb-6">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                    {t("upgrade_modal.free_section.choose_picture")}
                  </label>
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    <button
                      onClick={() => setSelectedImageId(null)}
                      className={`aspect-square rounded-lg border-2 flex items-center justify-center text-xs font-medium transition-all ${
                        selectedImageId === null
                          ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                          : "border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      {t("upgrade_modal.free_section.none")}
                    </button>
                    {availableImages.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => setSelectedImageId(img.id)}
                        className={`aspect-square rounded-lg border-2 overflow-hidden transition-all ${
                          selectedImageId === img.id
                            ? "border-indigo-500 ring-2 ring-indigo-200 dark:ring-indigo-800"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                        }`}
                      >
                        <img
                          src={img.preview_url || img.url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  {selectedImageId && (
                    <p className="text-xs text-slate-600 dark:text-slate-400 italic">
                      {t("upgrade_modal.free_section.testimonial_consent")}
                    </p>
                  )}
                </div>
              )}

              {hasShared && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-400 font-medium text-center">
                    ✓ {t("upgrade_modal.free_section.already_shared")}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <LinkedinShareButton
                  url={urlToShare}
                  title={shareMessage}
                  summary={shareMessage}
                  source="Photographe.ai"
                  className="w-full"
                  onClick={handleShare}
                >
                  <div className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors font-medium">
                    <LinkedinIcon size={20} round />
                    {t("upgrade_modal.free_section.share_linkedin")}
                  </div>
                </LinkedinShareButton>

                <FacebookShareButton
                  url={urlToShare}
                  hashtag="#PhotographeAI"
                  className="w-full"
                  onClick={handleShare}
                >
                  <div className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors font-medium">
                    <FacebookIcon size={20} round />
                    {t("upgrade_modal.free_section.share_facebook")}
                  </div>
                </FacebookShareButton>

                <TwitterShareButton
                  url={urlToShare}
                  title={shareMessage}
                  hashtags={["PhotographeAI"]}
                  className="w-full"
                  onClick={handleShare}
                >
                  <div className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-sky-50 dark:bg-sky-900/20 text-sky-600 dark:text-sky-400 hover:bg-sky-100 dark:hover:bg-sky-900/30 transition-colors font-medium">
                    <XIcon size={20} round />
                    {t("upgrade_modal.free_section.share_twitter")}
                  </div>
                </TwitterShareButton>

                {selectedImage?.url && (
                  <PinterestShareButton
                    url={urlToShare}
                    media={selectedImage?.url || ""}
                    description={shareMessage}
                    className="w-full"
                    onClick={handleShare}
                  >
                    <div className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium">
                      <PinterestIcon size={20} round />
                      {t("upgrade_modal.free_section.share_pinterest")}
                    </div>
                  </PinterestShareButton>
                )}

                <VKShareButton
                  url={urlToShare}
                  title={shareMessage}
                  image={selectedImage?.url}
                  className="w-full"
                  onClick={handleShare}
                >
                  <div className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors font-medium">
                    <VKIcon size={20} round />
                    {t("upgrade_modal.free_section.share_vk")}
                  </div>
                </VKShareButton>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Premium Option */}
        <div
          className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-8 text-white flex flex-col cursor-pointer hover:brightness-110 transition-all"
          onClick={handleUpgrade}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">
                {t("upgrade_modal.premium_section.title")}
              </h3>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-white/80">
                  {t("upgrade_modal.premium_section.starting_from")}
                </span>
                <span className="text-3xl font-bold">€9</span>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <div>
                  <p className="font-medium">
                    {t("upgrade_modal.premium_section.feature_quality_title")}
                  </p>
                  <p className="text-sm text-white/80">
                    {plan === "free"
                      ? t("upgrade_modal.premium_section.feature_quality_free")
                      : t(
                          "upgrade_modal.premium_section.feature_quality_premium"
                        )}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <div>
                  <p className="font-medium">
                    {t("upgrade_modal.premium_section.feature_pictures_title")}
                  </p>
                  <p className="text-sm text-white/80">
                    {t("upgrade_modal.premium_section.feature_pictures_desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <div>
                  <p className="font-medium">
                    {t("upgrade_modal.premium_section.feature_advanced_title")}
                  </p>
                  <p className="text-sm text-white/80">
                    {t("upgrade_modal.premium_section.feature_advanced_desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" />
                </div>
                <div>
                  <p className="font-medium">
                    {t("upgrade_modal.premium_section.feature_license_title")}
                  </p>
                  <p className="text-sm text-white/80">
                    {t("upgrade_modal.premium_section.feature_license_desc")}
                  </p>
                </div>
              </div>
            </div>

            {!minimumCredits && minimumPlan && (
              <div className="mb-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                <p className="text-sm">
                  {t("upgrade_modal.premium_section.requires_plan")
                    .split("<bold>")
                    .map((part, i) =>
                      i === 0 ? (
                        part
                      ) : (
                        <>
                          <span className="font-semibold">
                            {part
                              .split("</bold>")[0]
                              .replace(
                                "{{plan}}",
                                minimumPlan.charAt(0).toUpperCase() +
                                  minimumPlan.slice(1)
                              )}
                          </span>
                          {part.split("</bold>")[1]}
                        </>
                      )
                    )}
                </p>
              </div>
            )}

            {!!minimumCredits && (
              <div className="mb-4 p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                <p className="text-sm">
                  {t("upgrade_modal.premium_section.requires_credits")
                    .split("<bold>")
                    .map((part, i) =>
                      i === 0 ? (
                        part
                      ) : (
                        <Fragment key={i}>
                          <span className="font-semibold">
                            {part
                              .split("</bold>")[0]
                              .replace(
                                "{{credits}}",
                                minimumCredits.toString()
                              )}
                          </span>
                          {part.split("</bold>")[1]}
                        </Fragment>
                      )
                    )}
                </p>
              </div>
            )}
          </div>

          <Button
            size="3"
            className="w-full !bg-white !text-indigo-600 hover:!bg-white/90 font-semibold"
            onClick={handleUpgrade}
          >
            {t("upgrade_modal.premium_section.upgrade_button")}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};
